import { setCapyShieldHue } from "./capyShield";
import { SITE_TUNING } from "./siteTuning";
import { createVaultCapyShieldElement } from "./vaultCapyShield";

const MASCOT_TUNING = SITE_TUNING.vaultBreakers.mascot;
const AVAILABLE_VAULT_SELECTOR = '[data-vault-state="available"]';
const ANY_VAULT_SELECTOR = "[data-vault-state]";
const COMPACT_VIEWPORT_QUERY = "(max-width: 720px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

type Point = {
    x: number;
    y: number;
};

type MotionKind = "running" | "climbing" | "dropping" | "settling";
type RunnerLocation = "seam" | "moving" | "perched";
type SeamAxis = "horizontal" | "vertical";
type HorizontalDirection = "left" | "right";

type SurfaceGeometry = {
    frameRect: DOMRect;
    frameWidth: number;
    frameHeight: number;
    scaleX: number;
    scaleY: number;
    height: number;
    horizontalSeams: number[];
    verticalSeams: number[];
    width: number;
};

type SeamPoint = {
    axis: SeamAxis;
    point: Point;
};

type TargetGeometry = {
    perch: Point;
    topCenter: Point;
    topLeft: Point;
    topRight: Point;
};

export type VaultCapyRunnerSyncOptions = {
    announcementActive?: boolean;
    enabled: boolean;
    chooserKey: string | null | undefined;
    hue: number | null | undefined;
};

const clamp = (value: number, minimum: number, maximum: number): number =>
    Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

const getDistance = (from: Point, to: Point): number => Math.hypot(to.x - from.x, to.y - from.y);

const getManhattanDistance = (from: Point, to: Point): number =>
    Math.abs(to.x - from.x) + Math.abs(to.y - from.y);

const uniqueSortedCoordinates = (coordinates: number[]): number[] =>
    coordinates
        .sort((left, right) => left - right)
        .filter(
            (coordinate, index, values) =>
                index === 0 || Math.abs(coordinate - (values[index - 1] ?? coordinate)) >= 1
        );

/**
 * Moves the active chooser's Capy Shield along the borders of the vault grid.
 * Routine room updates retain the grid and overlay. After a surface replacement,
 * `sync` restores the overlay while root-level hover/focus listeners stay stable.
 */
export class VaultCapyRunner {
    private readonly root: HTMLElement;
    private readonly compactViewportQuery: MediaQueryList | null;
    private readonly reducedMotionQuery: MediaQueryList | null;

    private frame: HTMLElement | null = null;
    private layer: HTMLDivElement | null = null;
    private runner: HTMLSpanElement | null = null;
    private shield: HTMLSpanElement | null = null;
    private enabled = false;
    private announcementActive = false;
    private chooserKey: string | null = null;
    private targetVaultKey: string | null = null;
    private lastVaultKey: string | null = null;
    private location: RunnerLocation = "seam";
    private position: Point = { x: 0, y: 0 };
    private horizontalDirection: HorizontalDirection | null = null;
    private lastFrameWidth = 0;
    private routeToken = 0;
    private animationTimerId: number | null = null;
    private animationResolve: ((completed: boolean) => void) | null = null;
    private landingTimerId: number | null = null;
    private hoverIntentTimerId: number | null = null;
    private hoverIntentVaultKey: string | null = null;
    private resizeAnimationFrameId: number | null = null;
    private sparkleTrailTimerId: number | null = null;
    private sparkleTrailRouteToken: number | null = null;
    private sparkleIndex = 0;
    private entryStart: Point | null = null;
    private readonly sparkleCleanupTimerIds = new Set<number>();

    constructor(root: HTMLElement) {
        this.root = root;
        this.compactViewportQuery =
            typeof window.matchMedia === "function" ? window.matchMedia(COMPACT_VIEWPORT_QUERY) : null;
        this.reducedMotionQuery =
            typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION_QUERY) : null;

        this.root.addEventListener("pointerover", this.handlePointerOver);
        this.root.addEventListener("focusin", this.handleFocusIn);
        window.addEventListener("resize", this.handleResize);
        this.compactViewportQuery?.addEventListener("change", this.handleViewportPreferenceChange);
        this.reducedMotionQuery?.addEventListener("change", this.handleViewportPreferenceChange);
    }

    sync({ announcementActive = false, enabled, chooserKey, hue }: VaultCapyRunnerSyncOptions): void {
        const normalizedChooserKey = chooserKey?.trim() || null;
        const chooserChanged = normalizedChooserKey !== this.chooserKey;
        const announcementChanged = announcementActive !== this.announcementActive;
        const nextFrame = enabled ? this.root.querySelector<HTMLElement>(".vault-board-frame") : null;
        const hasAvailableVault = Boolean(nextFrame?.querySelector(AVAILABLE_VAULT_SELECTOR));
        const shouldShow = Boolean(enabled && normalizedChooserKey && nextFrame && hasAvailableVault);

        this.enabled = shouldShow;
        this.announcementActive = announcementActive;
        this.chooserKey = normalizedChooserKey;

        if (!shouldShow || !nextFrame) {
            this.cancelHoverIntent();
            this.targetVaultKey = null;
            this.removeSurface();
            return;
        }

        const surfaceChanged = nextFrame !== this.frame || !this.layer?.isConnected || !this.runner?.isConnected;
        const previousFrameWidth = this.lastFrameWidth;
        const previousRenderedPosition = this.sampleRenderedPosition();
        const previousCenterRatio =
            previousFrameWidth > 0
                ? clamp((previousRenderedPosition.x + this.getMascotSize() / 2) / previousFrameWidth, 0, 1)
                : 0.5;

        if (surfaceChanged) {
            this.removeSurface({ preserveTarget: true });
            this.createSurface(nextFrame, hue);
        } else if (this.shield) {
            setCapyShieldHue(this.shield, hue);
            this.applyMascotSize();
        }

        if (!this.runner || !this.layer) {
            return;
        }

        this.runner.classList.toggle("is-reduced-motion", this.prefersReducedMotion());

        if (surfaceChanged || chooserChanged || announcementChanged) {
            // Host-board renders replace the grid while announcements are active. Restore the last
            // hovered vault as an idle perch instead of reconstructing (and visibly restarting) a route.
            this.restoreIdlePosition(previousCenterRatio);
        }
    }

    destroy(): void {
        this.enabled = false;
        this.root.removeEventListener("pointerover", this.handlePointerOver);
        this.root.removeEventListener("focusin", this.handleFocusIn);
        window.removeEventListener("resize", this.handleResize);
        this.compactViewportQuery?.removeEventListener("change", this.handleViewportPreferenceChange);
        this.reducedMotionQuery?.removeEventListener("change", this.handleViewportPreferenceChange);
        if (this.resizeAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.resizeAnimationFrameId);
            this.resizeAnimationFrameId = null;
        }
        this.cancelHoverIntent();
        this.targetVaultKey = null;
        this.lastVaultKey = null;
        this.removeSurface();
    }

    /** Freeze the live route and leap only when the selected vault is within reach. */
    prepareVaultEntry(vaultKey: string): Point | null {
        const vault = this.findAvailableVault(vaultKey);
        const surface = this.measureSurface();
        const runner = this.runner;
        if (!this.enabled || !vault || !surface || !runner || this.prefersReducedMotion()) return null;
        const rect = vault.getBoundingClientRect();
        const left = (rect.left - surface.frameRect.left) / surface.scaleX;
        const top = (rect.top - surface.frameRect.top) / surface.scaleY;
        const width = rect.width / surface.scaleX;
        const height = rect.height / surface.scaleY;
        const from = this.sampleRenderedPosition();
        const center = { x: from.x + surface.width / 2, y: from.y + surface.height / 2 };
        const edgeDistance = Math.hypot(center.x - clamp(center.x, left, left + width),
            center.y - clamp(center.y, top, top + height));
        const destination = { x: left + width / 2, y: top + height / 2 };
        if (edgeDistance > this.getMascotSize() * MASCOT_TUNING.entryReachSizeRatio ||
            getDistance(center, destination) > Math.max(width, height) * 0.75 + this.getMascotSize()) return null;

        this.cancelHoverIntent();
        this.cancelRoute();
        this.entryStart = from;
        this.setMotionClass(null);
        this.setPosition(from);
        const end = { x: destination.x - surface.width / 2, y: destination.y - surface.height / 2 };
        const apex = { x: (from.x + end.x) / 2, y: Math.min(from.y, end.y) - MASCOT_TUNING.entryLeapHeightPx };
        runner.style.setProperty("--vault-capy-leap-start", this.renderTransform(from));
        runner.style.setProperty("--vault-capy-leap-apex", `${this.renderTransform(apex)} scale(1.08)`);
        runner.style.setProperty("--vault-capy-leap-land", `${this.renderTransform(end)} scale(0.8)`);
        runner.style.setProperty("--vault-capy-leap-end", `${this.renderTransform(end)} scale(0.12)`);
        runner.classList.add("is-vault-leaping");
        return destination;
    }

    cancelVaultEntry(): void {
        if (!this.entryStart) return;
        this.runner?.classList.remove("is-vault-leaping");
        for (const part of ["start", "apex", "land", "end"]) this.runner?.style.removeProperty(`--vault-capy-leap-${part}`);
        this.setPosition(this.entryStart);
        this.entryStart = null;
        this.location = "seam";
        this.targetVaultKey = null;
    }

    private readonly handlePointerOver = (event: PointerEvent): void => {
        this.handleVaultPointerIntent(event.target);
    };

    private readonly handleFocusIn = (event: FocusEvent): void => {
        this.cancelHoverIntent();
        this.handleVaultIntent(event.target);
    };

    private readonly handleResize = (): void => {
        if (!this.enabled || this.resizeAnimationFrameId !== null) {
            return;
        }

        this.resizeAnimationFrameId = window.requestAnimationFrame(() => {
            this.resizeAnimationFrameId = null;
            this.reflowSurface();
        });
    };

    private readonly handleViewportPreferenceChange = (): void => {
        this.handleResize();
    };

    private handleVaultPointerIntent(eventTarget: EventTarget | null): void {
        if (!this.enabled || this.entryStart || this.announcementActive || !(eventTarget instanceof Element)) {
            this.cancelHoverIntent();
            return;
        }

        const vault = eventTarget.closest<HTMLElement>(AVAILABLE_VAULT_SELECTOR);
        if (!vault || !this.root.contains(vault) || !this.frame?.contains(vault)) {
            this.cancelHoverIntent();
            return;
        }

        const vaultKey = this.getVaultKey(vault);
        if (!vaultKey || vaultKey === this.hoverIntentVaultKey) {
            return;
        }

        this.cancelHoverIntent();
        this.hoverIntentVaultKey = vaultKey;
        this.hoverIntentTimerId = window.setTimeout(() => {
            this.hoverIntentTimerId = null;
            this.hoverIntentVaultKey = null;

            if (this.announcementActive) {
                return;
            }

            const settledVault = this.findAvailableVault(vaultKey);
            if (settledVault) {
                this.requestVault(settledVault);
            }
        }, MASCOT_TUNING.hoverIntentMs);
    }

    private handleVaultIntent(eventTarget: EventTarget | null): void {
        if (!this.enabled || this.entryStart || this.announcementActive || !(eventTarget instanceof Element)) {
            return;
        }

        const vault = eventTarget.closest<HTMLElement>(AVAILABLE_VAULT_SELECTOR);
        if (!vault || !this.root.contains(vault) || !this.frame?.contains(vault)) {
            return;
        }

        this.requestVault(vault);
    }

    private createSurface(frame: HTMLElement, hue: number | null | undefined): void {
        const layer = document.createElement("div");
        const runner = document.createElement("span");
        const shield = createVaultCapyShieldElement({ className: "vault-capy-runner-shield", hue });

        layer.className = "vault-capy-runner-layer";
        layer.setAttribute("aria-hidden", "true");
        layer.style.position = "absolute";
        layer.style.inset = "0";
        // Keep the selector guide above board announcements; it remains non-interactive and never steals a click.
        layer.style.zIndex = "6";
        layer.style.overflow = "visible";
        layer.style.pointerEvents = "none";

        runner.className = "vault-capy-runner";
        runner.dataset.path = "vault-borders";
        runner.setAttribute("aria-hidden", "true");
        runner.style.position = "absolute";
        runner.style.top = "0";
        runner.style.left = "0";
        runner.style.display = "block";
        runner.style.pointerEvents = "none";
        runner.style.willChange = "transform";

        shield.style.display = "block";
        runner.append(shield);
        layer.append(runner);
        frame.append(layer);

        this.frame = frame;
        this.layer = layer;
        this.runner = runner;
        this.shield = shield;
        this.applyMascotSize();
    }

    private removeSurface(options: { preserveTarget?: boolean } = {}): void {
        this.cancelVaultEntry();
        this.cancelHoverIntent();
        this.cancelRoute();
        this.clearSparkleTrail();
        if (this.landingTimerId !== null) {
            window.clearTimeout(this.landingTimerId);
            this.landingTimerId = null;
        }
        this.layer?.remove();
        // Clean up the superseded header-rail version during live refreshes.
        this.frame?.classList.remove("has-capy-runner");
        this.frame?.style.removeProperty("--vault-capy-size");
        this.frame?.style.removeProperty("--vault-capy-rail-y");
        this.frame = null;
        this.layer = null;
        this.runner = null;
        this.shield = null;
        this.location = "seam";
        this.lastFrameWidth = 0;
        if (!options.preserveTarget) {
            this.targetVaultKey = null;
        }
    }

    private applyMascotSize(): void {
        if (!this.runner || !this.shield) {
            return;
        }

        const size = this.getMascotSize();
        this.runner.style.setProperty("--vault-capy-size", `${MASCOT_TUNING.sizePx}px`);
        this.runner.style.setProperty("--vault-capy-compact-size", `${MASCOT_TUNING.compactSizePx}px`);
        this.runner.style.width = `${size}px`;
        this.runner.style.height = `${size}px`;
        this.shield.style.width = `${size}px`;
        this.shield.style.height = `${size}px`;
    }

    private getMascotSize(): number {
        return this.compactViewportQuery?.matches ? MASCOT_TUNING.compactSizePx : MASCOT_TUNING.sizePx;
    }

    private prefersReducedMotion(): boolean {
        return Boolean(this.reducedMotionQuery?.matches);
    }

    private requestVault(vault: HTMLElement): void {
        const vaultKey = this.getVaultKey(vault);
        if (!vaultKey || (vaultKey === this.targetVaultKey && this.location !== "seam")) {
            return;
        }

        const originVaultKey = this.location === "perched" ? this.targetVaultKey : null;
        const originLocation = this.location;
        const originDirection = this.horizontalDirection;
        const start = this.sampleRenderedPosition();
        this.routeToken += 1;
        const token = this.routeToken;
        this.cancelActiveAnimation(start);
        this.clearLandingState();
        this.targetVaultKey = vaultKey;
        this.lastVaultKey = vaultKey;

        if (this.prefersReducedMotion()) {
            const targetGeometry = this.measureTarget(vault);
            if (targetGeometry) {
                this.setPosition(targetGeometry.perch);
                this.location = "perched";
                this.setMotionClass(null);
                this.runner?.classList.add("is-perched");
            }
            return;
        }

        void this.routeToVault(vaultKey, start, token, originVaultKey, originLocation, originDirection);
    }

    private cancelHoverIntent(): void {
        if (this.hoverIntentTimerId !== null) {
            window.clearTimeout(this.hoverIntentTimerId);
            this.hoverIntentTimerId = null;
        }
        this.hoverIntentVaultKey = null;
    }

    private restoreIdlePosition(horizontalRatio: number): void {
        this.cancelHoverIntent();
        const lastVault = this.lastVaultKey ? this.findVault(this.lastVaultKey) : null;
        const targetGeometry = lastVault ? this.measureTarget(lastVault) : null;

        if (!lastVault || !targetGeometry || !this.lastVaultKey) {
            this.targetVaultKey = null;
            this.placeOnGridSeam(horizontalRatio);
            return;
        }

        this.cancelRoute();
        this.targetVaultKey = this.lastVaultKey;
        this.setPosition(targetGeometry.perch);
        this.location = "perched";
        this.setMotionClass(null);
        this.runner?.classList.add("is-perched");
    }

    private async routeToVault(
        vaultKey: string,
        start: Point,
        token: number,
        originVaultKey: string | null,
        originLocation: RunnerLocation,
        originDirection: HorizontalDirection | null
    ): Promise<void> {
        const vault = this.findAvailableVault(vaultKey);
        const surface = this.measureSurface();
        const target = vault ? this.measureTarget(vault) : null;
        if (!vault || !surface || !target || !this.isCurrentRoute(vaultKey, token)) {
            return;
        }

        this.location = "moving";
        this.runner?.classList.remove("is-perched");

        let seamOrigin: SeamPoint;
        const originVault = originLocation === "perched" && originVaultKey
            ? this.findAvailableVault(originVaultKey)
            : null;
        const originTarget = originVault ? this.measureTarget(originVault) : null;

        if (originTarget) {
            const targetDirection = this.getHorizontalDirection(start, target.topCenter);
            const exitCorner = this.pickHorizontalEdge(
                start,
                originTarget.topLeft,
                originTarget.topRight,
                targetDirection ?? originDirection
            );
            if (!(await this.animateTo(exitCorner, "running", MASCOT_TUNING.runPxPerSecond, token))) {
                return;
            }
            seamOrigin = { axis: "horizontal", point: exitCorner };
        } else {
            seamOrigin = this.projectToNearestSeam(start, surface);
            if (!(await this.animateTo(seamOrigin.point, "running", MASCOT_TUNING.runPxPerSecond, token))) {
                return;
            }
        }

        const refreshedVault = this.findAvailableVault(vaultKey);
        const refreshedTarget = refreshedVault ? this.measureTarget(refreshedVault) : null;
        if (!refreshedTarget || !this.isCurrentRoute(vaultKey, token)) {
            return;
        }

        const targetCorner = this.pickHorizontalEdge(
            seamOrigin.point,
            refreshedTarget.topLeft,
            refreshedTarget.topRight,
            originLocation === "moving" ? originDirection : null
        );

        if (seamOrigin.axis === "horizontal") {
            const bend = { x: targetCorner.x, y: seamOrigin.point.y };
            if (!(await this.animateTo(bend, "running", MASCOT_TUNING.runPxPerSecond, token))) {
                return;
            }
            if (!(await this.animateVertical(targetCorner, token))) {
                return;
            }
        } else {
            const bend = { x: seamOrigin.point.x, y: targetCorner.y };
            if (!(await this.animateVertical(bend, token))) {
                return;
            }
            if (!(await this.animateTo(targetCorner, "running", MASCOT_TUNING.runPxPerSecond, token))) {
                return;
            }
        }

        // One eased approach avoids a stop at top-center followed by a tiny vertical correction.
        if (!(await this.animateTo(refreshedTarget.perch, "settling", MASCOT_TUNING.runPxPerSecond, token))) {
            return;
        }

        if (!this.isCurrentRoute(vaultKey, token)) {
            return;
        }

        this.stopSparkleTrail();
        this.location = "perched";
        this.setMotionClass(null);
        this.runner?.classList.add("is-landing", "is-perched");
        this.landingTimerId = window.setTimeout(() => {
            this.landingTimerId = null;
            this.runner?.classList.remove("is-landing");
        }, MASCOT_TUNING.landingMs);
    }

    private animateVertical(target: Point, token: number): Promise<boolean> {
        const from = this.sampleRenderedPosition();
        const movingUp = target.y < from.y;

        return this.animateTo(
            target,
            movingUp ? "climbing" : "dropping",
            movingUp ? MASCOT_TUNING.climbPxPerSecond : MASCOT_TUNING.dropPxPerSecond,
            token
        );
    }

    private animateTo(target: Point, kind: MotionKind, pixelsPerSecond: number, token: number): Promise<boolean> {
        const runner = this.runner;
        if (!runner || token !== this.routeToken) {
            return Promise.resolve(false);
        }

        const from = this.sampleRenderedPosition();
        const distance = getDistance(from, target);
        if (distance < 0.5) {
            this.setPosition(target);
            return Promise.resolve(true);
        }

        const duration = Math.max(MASCOT_TUNING.minSegmentMs, (distance / pixelsPerSecond) * 1000);
        this.setFacing(target.x - from.x);
        this.setMotionClass(kind);
        this.startSparkleTrail(token);
        runner.style.transition = "none";
        this.setPosition(from);
        runner.getBoundingClientRect();
        runner.style.transition = `transform ${duration}ms ${kind === "settling" ? "ease-out" : "linear"}`;
        runner.style.transform = this.renderTransform(target);

        return new Promise<boolean>((resolve) => {
            this.animationResolve = resolve;
            this.animationTimerId = window.setTimeout(() => {
                this.animationTimerId = null;
                this.animationResolve = null;
                if (token !== this.routeToken) {
                    resolve(false);
                    return;
                }
                this.position = target;
                runner.style.transition = "none";
                runner.style.transform = this.renderTransform(target);
                resolve(true);
            }, duration + 24);
        });
    }

    private cancelRoute(): void {
        this.routeToken += 1;
        this.cancelActiveAnimation(this.sampleRenderedPosition());
        this.clearLandingState();
    }

    private cancelActiveAnimation(renderedPosition: Point): void {
        this.stopSparkleTrail();
        if (this.animationTimerId !== null) {
            window.clearTimeout(this.animationTimerId);
            this.animationTimerId = null;
        }
        const resolve = this.animationResolve;
        this.animationResolve = null;
        resolve?.(false);

        this.position = renderedPosition;
        if (this.runner) {
            this.runner.style.transition = "none";
            this.runner.style.transform = this.renderTransform(renderedPosition);
        }
    }

    private clearLandingState(): void {
        if (this.landingTimerId !== null) {
            window.clearTimeout(this.landingTimerId);
            this.landingTimerId = null;
        }
        this.runner?.classList.remove("is-landing", "is-perched", "is-settling");
    }

    private isCurrentRoute(vaultKey: string, token: number): boolean {
        return Boolean(this.enabled && token === this.routeToken && vaultKey === this.targetVaultKey);
    }

    private reflowSurface(): void {
        if (!this.enabled || !this.frame || !this.runner || !this.shield) {
            return;
        }

        this.cancelRoute();
        this.applyMascotSize();
        this.runner.classList.toggle("is-reduced-motion", this.prefersReducedMotion());
        this.restoreIdlePosition(0.5);
    }

    private placeOnGridSeam(horizontalRatio: number, verticalRatio = 0.5): void {
        const surface = this.measureSurface();
        if (!surface) {
            return;
        }

        const centerX = this.findNearestCoordinate(
            surface.verticalSeams,
            surface.frameWidth * horizontalRatio
        );
        const centerY = this.findNearestCoordinate(
            surface.horizontalSeams,
            surface.frameHeight * verticalRatio
        );
        const point = this.toRunnerPoint(centerX, centerY, surface);
        point.y = clamp(
            point.y + MASCOT_TUNING.boardVaultInsetPx,
            0,
            surface.frameHeight - surface.height
        );

        this.cancelRoute();
        this.setPosition(point);
        this.location = "seam";
        this.setMotionClass(null);
    }

    private measureSurface(): SurfaceGeometry | null {
        const frame = this.frame;
        if (!frame) {
            return null;
        }

        const frameRect = frame.getBoundingClientRect();
        const scaleX = frameRect.width / frame.offsetWidth || 1;
        const scaleY = frameRect.height / frame.offsetHeight || 1;
        const vaultRects = Array.from(frame.querySelectorAll<HTMLElement>(ANY_VAULT_SELECTOR))
            .map((vault) => vault.getBoundingClientRect())
            .filter((rect) => rect.width > 0 && rect.height > 0);
        if (frameRect.width <= 0 || vaultRects.length === 0) {
            return null;
        }

        const size = this.getMascotSize();
        const verticalSeams = uniqueSortedCoordinates(
            vaultRects.flatMap((rect) => [(rect.left - frameRect.left) / scaleX, (rect.right - frameRect.left) / scaleX])
        );
        const horizontalSeams = uniqueSortedCoordinates(
            vaultRects.flatMap((rect) => [(rect.top - frameRect.top) / scaleY, (rect.bottom - frameRect.top) / scaleY])
        );

        this.lastFrameWidth = frame.clientWidth;
        return {
            frameRect,
            frameWidth: frame.clientWidth,
            frameHeight: frame.clientHeight,
            scaleX,
            scaleY,
            height: size,
            horizontalSeams,
            verticalSeams,
            width: size
        };
    }

    private measureTarget(vault: HTMLElement): TargetGeometry | null {
        const surface = this.measureSurface();
        if (!surface) {
            return null;
        }

        const vaultRect = vault.getBoundingClientRect();
        if (vaultRect.width <= 0) {
            return null;
        }

        const tileLeft = (vaultRect.left - surface.frameRect.left) / surface.scaleX;
        const tileRight = (vaultRect.right - surface.frameRect.left) / surface.scaleX;
        const tileTop = (vaultRect.top - surface.frameRect.top) / surface.scaleY;
        const tileCenterX = (tileLeft + tileRight) / 2;

        return {
            topLeft: this.toRunnerPoint(tileLeft, tileTop, surface),
            topRight: this.toRunnerPoint(tileRight, tileTop, surface),
            topCenter: this.toRunnerPoint(tileCenterX, tileTop, surface),
            perch: {
                x: clamp(tileCenterX - surface.width / 2, 0, surface.frameWidth - surface.width),
                y: clamp(
                    tileTop - surface.height * 0.72 + MASCOT_TUNING.boardVaultInsetPx,
                    0,
                    surface.frameHeight - surface.height
                )
            }
        };
    }

    private projectToNearestSeam(position: Point, surface: SurfaceGeometry): SeamPoint {
        const centerX = position.x + surface.width / 2;
        const centerY = position.y + surface.height / 2;
        const horizontalY = this.findNearestCoordinate(surface.horizontalSeams, centerY);
        const verticalX = this.findNearestCoordinate(surface.verticalSeams, centerX);
        const horizontalDistance = Math.abs(horizontalY - centerY);
        const verticalDistance = Math.abs(verticalX - centerX);

        if (horizontalDistance <= verticalDistance) {
            return {
                axis: "horizontal",
                point: this.toRunnerPoint(centerX, horizontalY, surface)
            };
        }

        return {
            axis: "vertical",
            point: this.toRunnerPoint(verticalX, centerY, surface)
        };
    }

    private toRunnerPoint(centerX: number, centerY: number, surface: SurfaceGeometry): Point {
        return {
            x: clamp(centerX - surface.width / 2, 0, surface.frameWidth - surface.width),
            y: clamp(centerY - surface.height / 2, 0, surface.frameHeight - surface.height)
        };
    }

    private findNearestCoordinate(coordinates: number[], value: number): number {
        if (coordinates.length === 0) {
            return value;
        }

        return coordinates.reduce((nearest, coordinate) =>
            Math.abs(coordinate - value) < Math.abs(nearest - value) ? coordinate : nearest
        );
    }

    private findAvailableVault(vaultKey: string): HTMLElement | null {
        const vault = this.findVault(vaultKey);
        return vault?.matches(AVAILABLE_VAULT_SELECTOR) ? vault : null;
    }

    private findVault(vaultKey: string): HTMLElement | null {
        if (!this.frame) {
            return null;
        }

        return (
            Array.from(this.frame.querySelectorAll<HTMLElement>(ANY_VAULT_SELECTOR)).find(
                (vault) => this.getVaultKey(vault) === vaultKey
            ) ?? null
        );
    }

    private getVaultKey(vault: HTMLElement): string | null {
        const explicitKey = vault.dataset.vaultId?.trim();
        if (explicitKey) {
            return explicitKey;
        }

        const vaults = this.frame ? Array.from(this.frame.querySelectorAll<HTMLElement>(ANY_VAULT_SELECTOR)) : [];
        const index = vaults.indexOf(vault);
        return index >= 0 ? `vault-index-${index}` : null;
    }

    private sampleRenderedPosition(): Point {
        if (!this.runner || !this.layer?.isConnected || !this.runner.isConnected) {
            return this.position;
        }

        // Read the local transform: viewport rectangles include ancestor zoom/scale
        // and feed that scale back into the next segment as a visible arrival jerk.
        const Matrix = window.DOMMatrixReadOnly;
        if (typeof Matrix !== "function") return this.position;
        const transform = new Matrix(window.getComputedStyle(this.runner).transform);
        return { x: transform.m41, y: transform.m42 };
    }

    private setPosition(position: Point): void {
        this.position = position;
        if (this.runner) {
            this.runner.style.transition = "none";
            this.runner.style.transform = this.renderTransform(position);
        }
    }

    private renderTransform(position: Point): string {
        return `translate3d(${position.x}px, ${position.y}px, 0)`;
    }

    private getHorizontalDirection(from: Point, to: Point): HorizontalDirection | null {
        const horizontalDelta = to.x - from.x;
        if (Math.abs(horizontalDelta) < 0.5) {
            return null;
        }
        return horizontalDelta < 0 ? "left" : "right";
    }

    private pickHorizontalEdge(
        from: Point,
        leftEdge: Point,
        rightEdge: Point,
        preferredDirection: HorizontalDirection | null
    ): Point {
        const leftDelta = leftEdge.x - from.x;
        const rightDelta = rightEdge.x - from.x;
        const edgesStraddleRunner = leftDelta <= 0 && rightDelta >= 0;

        // Retargeting mid-run should not make Capy reverse across the face of a vault when either edge is valid.
        // Once perched, the caller instead supplies the direction of the next vault so Capy leaves from its near side.
        if (preferredDirection && edgesStraddleRunner) {
            return preferredDirection === "left" ? leftEdge : rightEdge;
        }

        return getManhattanDistance(from, leftEdge) <= getManhattanDistance(from, rightEdge) ? leftEdge : rightEdge;
    }

    private setFacing(horizontalDelta: number): void {
        if (!this.runner || Math.abs(horizontalDelta) < 0.5) {
            return;
        }
        this.horizontalDirection = horizontalDelta < 0 ? "left" : "right";
        this.runner.classList.toggle("is-facing-left", this.horizontalDirection === "left");
        this.runner.classList.toggle("is-facing-right", this.horizontalDirection === "right");
    }

    private setMotionClass(kind: MotionKind | null): void {
        if (!this.runner) {
            return;
        }

        this.runner.classList.remove("is-running", "is-climbing", "is-dropping", "is-settling");
        if (kind) {
            this.runner.classList.add(`is-${kind}`);
        }
        this.runner.dataset.motion = kind ?? "idle";
    }

    private startSparkleTrail(token: number): void {
        if (this.prefersReducedMotion()) {
            return;
        }
        if (this.sparkleTrailTimerId !== null && this.sparkleTrailRouteToken === token) {
            return;
        }

        this.stopSparkleTrail();
        this.sparkleTrailRouteToken = token;
        this.emitSparkle();
        this.sparkleTrailTimerId = window.setInterval(() => {
            if (token !== this.routeToken || !this.runner?.isConnected || !this.layer?.isConnected) {
                this.stopSparkleTrail();
                return;
            }
            this.emitSparkle();
        }, MASCOT_TUNING.sparkleTrailIntervalMs);
    }

    private stopSparkleTrail(): void {
        if (this.sparkleTrailTimerId !== null) {
            window.clearInterval(this.sparkleTrailTimerId);
            this.sparkleTrailTimerId = null;
        }
        this.sparkleTrailRouteToken = null;
    }

    private clearSparkleTrail(): void {
        this.stopSparkleTrail();
        this.sparkleCleanupTimerIds.forEach((timerId) => window.clearTimeout(timerId));
        this.sparkleCleanupTimerIds.clear();
        this.layer?.querySelectorAll(".vault-capy-trail-spark").forEach((sparkle) => sparkle.remove());
    }

    private emitSparkle(): void {
        const layer = this.layer;
        const runner = this.runner;
        if (!layer || !runner || this.prefersReducedMotion()) {
            return;
        }

        const position = this.sampleRenderedPosition();
        const size = this.getMascotSize();

        const sparkle = document.createElement("span");
        const variantIndex = this.sparkleIndex % 6;
        const sizes = [8.2, 6.2, 9.4, 7, 8.8, 5.6];
        const verticalOffsets = [0.06, -0.1, 0.15, -0.03, 0.1, -0.14];
        const driftX = [-10, 7, -6, 10, -9, 6];
        const driftY = [-12, -16, -9, -14, -11, -17];
        const trailSide = this.horizontalDirection === "left" ? 0.74 : 0.26;
        const sparkleX = position.x + size * trailSide;
        const sparkleY =
            position.y + size * (0.7 + verticalOffsets[variantIndex]!);

        this.sparkleIndex += 1;
        sparkle.className = "vault-capy-trail-spark";
        sparkle.style.left = `${sparkleX}px`;
        sparkle.style.top = `${sparkleY}px`;
        sparkle.style.setProperty("--vault-capy-spark-size", `${sizes[variantIndex]}px`);
        sparkle.style.setProperty("--vault-capy-spark-drift-x", `${driftX[variantIndex]}px`);
        sparkle.style.setProperty("--vault-capy-spark-drift-y", `${driftY[variantIndex]}px`);
        sparkle.style.setProperty("--vault-capy-spark-turn", `${variantIndex % 2 === 0 ? 28 : -24}deg`);
        sparkle.style.animationDuration = `${MASCOT_TUNING.sparkleTrailLifetimeMs}ms`;
        layer.prepend(sparkle);

        const cleanupTimerId = window.setTimeout(() => {
            sparkle.remove();
            this.sparkleCleanupTimerIds.delete(cleanupTimerId);
        }, MASCOT_TUNING.sparkleTrailLifetimeMs + 80);
        this.sparkleCleanupTimerIds.add(cleanupTimerId);
    }
}
